import { flow, Instance, SnapshotOut, types } from 'mobx-state-tree';
import { CHATBOT_TYPE } from 'src/assets/static/constants';
import { withRootStore } from 'src/models/extensions/withRootStore';
import { withSetPropAction } from 'src/models/extensions/withSetPropAction';

import { Chatbot, IChatbot } from '../Chatbot';
import { IOption } from '../user-option/UserOption';

/**
 * # ConversationStore
 *
 * 챗봇 - 사용자 대화용 모델
 */

export const Conversation = types.model('conversation', {
  speaker: types.maybeNull(types.enumeration(Object.values(CHATBOT_TYPE.SPEAKER))),
  chatbotMessage: types.maybeNull(Chatbot),
  userMessage: types.maybeNull(types.string), // 사용자 선택 option 값
  isShowIcon: types.maybeNull(types.boolean),
  isLoading: types.optional(types.boolean, false), // 메시지 로딩 상태
});

type TConversation = Instance<typeof Conversation>;
export interface IConversation extends TConversation {}
export const createConversation = () => types.optional(Conversation, {} as TConversation);

export const ConversationStore = types
  .model('ConversationStore')
  // --------------------------------------------------------------------------
  .props({
    conversations: types.array(Conversation),
  })
  .extend(withRootStore)
  .actions(withSetPropAction)
  .views((self) => ({
    get conversationList() {
      return self.conversations;
    },
  }))
  // --------------------------------------------------------------------------
  // MUTATEs - 모델 상태를 변경
  .actions((self) => {
    const addConversation = (conversation: IConversation) => {
      self.conversations.push(conversation);
    };

    return {
      addUserConversation(option: IOption, conversationIndex?: number, selectedValues?: string[]) {
        if (conversationIndex) {
          // 현재 conversation의 chatbotMessage 찾기
          const currentConversation = self.conversations[conversationIndex];
          if (!currentConversation?.chatbotMessage) return;

          // chatbotMessage의 selectedOptions 업데이트
          if (selectedValues) {
            // 중복 선택 값
            currentConversation.chatbotMessage.setProps({
              selectedOptions: selectedValues,
            });
          } else if (option && option.value) {
            // 단일 선택 값
            currentConversation.chatbotMessage.setProps({
              selectedOptions: [option.value],
            });
          }
        }

        // conversation에 사용자 응답 추가
        addConversation({
          speaker: CHATBOT_TYPE.SPEAKER.USER,
          userMessage: option.option,
          chatbotMessage: null,
          isShowIcon: false,
          isLoading: false,
        });
      },

      // API 요청 전 로딩 대화 추가
      addLoadingConversation(isFirstMessage: boolean) {
        addConversation({
          speaker: CHATBOT_TYPE.SPEAKER.CHATBOT,
          chatbotMessage: null,
          userMessage: null,
          isShowIcon: isFirstMessage,
          isLoading: true,
        });
      },

      // API 응답 후 로딩 대화 업데이트
      updateLoadingConversation(chatbotMessage: IChatbot) {
        // 마지막 대화 찾기
        const lastIndex = self.conversations.length - 1;

        // 마지막 대화가 로딩 상태인지 확인
        if (lastIndex >= 0 && self.conversations[lastIndex].isLoading) {
          const isCard = chatbotMessage.conv_type === CHATBOT_TYPE.CONV_TYPE.CARD;
          const loadingConversation = self.conversations[lastIndex];

          // 마지막 대화를 업데이트
          self.conversations[lastIndex] = {
            speaker: CHATBOT_TYPE.SPEAKER.CHATBOT,
            chatbotMessage: chatbotMessage,
            userMessage: null,
            isShowIcon: isCard ? false : loadingConversation.isShowIcon,
            isLoading: false,
          };
        }
      },

      // 로딩 대화 제거
      removeLoadingConversation() {
        // 마지막 대화 찾기
        const lastIndex = self.conversations.length - 1;

        // 마지막 대화가 로딩 상태인지 확인 후 제거
        if (lastIndex >= 0 && self.conversations[lastIndex].isLoading) {
          self.conversations.splice(lastIndex, 1);
        }
      },

      // setTimeout으로 대화 추가 (비동기 처리)
      addDelayedConversation: flow(function* (conversation, delay) {
        yield new Promise((resolve) => setTimeout(resolve, delay));
        addConversation(conversation);
      }),

      addChatbotConversation(chatbotMessage: IChatbot, isFirstMessage: boolean) {
        const messages = chatbotMessage.chatbot_response?.message || [];
        const isCard = chatbotMessage.conv_type === CHATBOT_TYPE.CONV_TYPE.CARD;
        const isPieChart = chatbotMessage.conv_type === CHATBOT_TYPE.CONV_TYPE.PIECHART;
        const isHistoryUser = chatbotMessage.conv_type === CHATBOT_TYPE.CONV_TYPE.HISTORY_USER;

        // history_user 처리 - UserContainer에 표시
        if (isHistoryUser && messages.length > 0) {
          addConversation({
            speaker: CHATBOT_TYPE.SPEAKER.USER,
            chatbotMessage: null,
            userMessage: messages[0],
            isShowIcon: false,
            isLoading: false,
          });
          return;
        }

        // 메시지가 없거나 한개이거나 conv_type이 card, pie-chart인 경우
        if (messages.length < 2 || isCard || isPieChart) {
          addConversation({
            speaker: CHATBOT_TYPE.SPEAKER.CHATBOT,
            chatbotMessage: chatbotMessage,
            userMessage: null,
            isShowIcon: isCard ? false : isFirstMessage,
            isLoading: false,
          });
          return;
        }

        // 여러개의 메시지 처리
        messages.forEach((message, index) => {
          const isLastMessage = index === messages.length - 1;
          const delay = 1000 * (index + 1);

          // 마지막 메시지는 옵션과 질문을 포함, 그 외에는 제거
          const messageData = {
            speaker: CHATBOT_TYPE.SPEAKER.CHATBOT,
            chatbotMessage: {
              ...chatbotMessage,
              chatbot_response: {
                message: [message],
              },
              user_option: isLastMessage ? chatbotMessage.user_option : undefined,
              questions: isLastMessage ? chatbotMessage.questions : undefined,
            },
            userMessage: null,
            // 첫 메시지에만 아이콘 표시
            isShowIcon: index === 0 && isFirstMessage,
            isLoading: false,
          };

          this.addDelayedConversation(messageData, delay);
        });
      },

      clearConversations() {
        self.conversations.clear();
      },

      // 챗봇 서버 에러 발생 시 사용자 선택값 초기화
      resetLastConversationSelectedOptions() {
        // 챗봇 메시지 마지막 인덱스 찾기
        const lastChatbotIndex = self.conversations.reduce(
          (lastIndex, conversation, currentIndex) => {
            return conversation.speaker === CHATBOT_TYPE.SPEAKER.CHATBOT ? currentIndex : lastIndex;
          },
          -1,
        );

        if (lastChatbotIndex >= 0 && self.conversations[lastChatbotIndex].chatbotMessage) {
          const chatbot = self.conversations[lastChatbotIndex].chatbotMessage as IChatbot;
          chatbot.setProps({
            selectedOptions: null,
          });
        }
      },
    };
  });

// --------------------------------------------------------------------------
type TConversationStore = Instance<typeof ConversationStore>;
type TConversationStoreSnapshot = SnapshotOut<typeof ConversationStore>;

export interface IConversationStore extends TConversationStore {}
export type TConversationStoreKeys = keyof TConversationStoreSnapshot & string;
export interface IConversationStoreSnapshot extends TConversationStoreSnapshot {}
export const createConversationStore = () =>
  types.optional(ConversationStore, {} as TConversationStore);
